<script>
    import firebase from 'firebase/app';
    import { user, profileData } from '../../../store';
    import { db, timestamp, functions } from '../../../firebase';
    import { collectionData } from 'rxfire/firestore';
    import { startWith } from 'rxjs/operators';
    import { createEventDispatcher } from 'svelte';

    import { incrementMessageCount } from '../../../helpers/messagecount'
    import { text_area_resize } from '../../../helpers/autoResizeTextarea.js'
    import Dayjs from "dayjs";
    import relativeTIme from "dayjs/plugin/relativeTime";
    import updateLocale from "dayjs/plugin/updateLocale";
    Dayjs.extend(relativeTIme);
    Dayjs.extend(updateLocale)
    Dayjs.updateLocale('en', {
        relativeTime: {
            future: "in %s",
            past: "%s ago",
            s: 'now',
            m: "1m",
            mm: "%dm",
            h: "1h",
            hh: "%dh",
            d: "1d",
            dd: "%dd",
            M: "1mo",
            MM: "%dmo",
            y: "1y",
            yy: "%dy"
        }
    })

	import ChatCard from "../../molecules/ChatCard/ChatCard.svelte";
    import Icon from '../../atoms/Icon.svelte';
	import arrow_right from '../../../../public/assets/svg/24/arrow-right.svg';

    export let docId;
    export let idea;
    export let authorData;
	export let ideaImageAbsolutePath;
    
    const dispatch = createEventDispatcher();
    
    let message = "";
    let hideSend = true;
    let chatUsers = {};

    let chatsQuery = db.collection('chats').where('idea', '==', docId);
    let messagesQuery = db.collection('messages').where('idea', '==', docId).orderBy('timestamp', 'asc');
    let messages = collectionData(messagesQuery, 'id').pipe(startWith([]));
    let messageToNotify;

    $: $messages && $messages.length && dispatch('scrollModalToLatest');
    $: (message.length) ? hideSend = false : hideSend = true;

    const addNewMessage = () => {
        messageToNotify = message;
        db.collection('messages').add({ 
            uid: $user.uid, 
            message, 
            timestamp: timestamp, 
            idea: docId
        })
        .then((docRef) => {
            message = ""; // reset message input
            dispatch('scrollModalToLatest');
            createOrUpdateChatroom();
        }).then(() => {
            // Notify chat members by email
            notifyChatMembersByEmail();
        })
        .catch((error) => console.log(error));
    }

    const notifyChatMembersByEmail = () => {
        // construct SendGrid personalizations array
        let usersToNotify = [];
        for (let [key, value] of Object.entries(chatUsers)) {
            usersToNotify = [...usersToNotify, {
                to: value.email,
                dynamic_template_data: templateData(value.name)
            }]
        }
        // add idea owner to notification receipants (use authorData)
        // except the owner is the one that has left this message that should trigger a notification.
        // and except the owner is already a chat member in his own idea.
        if(authorData.uid !== $profileData.uid && !Object.keys(chatUsers).includes(authorData.uid)) {
            usersToNotify = [...usersToNotify,{
                to: authorData.email,
                dynamic_template_data: templateData(authorData.name)
            }]
        }
        // send notification to idea owner and all chat members except to the person that submitted this very message. It wouldn't make sense to send a notification to a message you just left yourself.
        const sendEmail = functions.httpsCallable('sendEmail');
        sendEmail({
            type: 'chat',
            personalizations: usersToNotify
        })
        .then(result => {
            messageToNotify = ""; // Reset message text to notify chat members
            // Read result of the Cloud Function.
            console.log(result.data);
        }).catch(error => {
            console.log(error);
        });
    }

    const templateData = (receiverName) => {
        return {
            receiver_name: receiverName, // email address to send to
            msg_author: $profileData.name, // current user name that submitted this message
            msg_text: messageToNotify, // message
            subject: idea.title.replace(/(.{35})..+/, "$1…"),
            idea_title: idea.title, // idea title
            idea_link: `https://app.originea.xyz/#/idea/${idea.id}?chat` // deep link to idea
        }
    }

    const createOrUpdateChatroom = () => {
        chatsQuery.get().then((snapshot) => {
            if(snapshot.docs.length) {
                snapshot.forEach((doc) => {
                    const docData = doc.data();
                    // increment message count
                    incrementMessageCount('chats', doc.id);
                    incrementMessageCount('ideas', docId);
                    // check if user is not yet in this chatroom, then add it
                    if(!docData.members.includes($user.uid)) {
                        db.collection('chats').doc(doc.id).update({
                            members: firebase.firestore.FieldValue.arrayUnion($user.uid)
                        })
                        .then(() => {
                            // Save chatroom to users document
                            saveChatroomToUserDoc(doc.id);
                            // console.log('user has been added to this chatroom');
                        })
                        .catch((error) => console.log(error));
                    }
                })
            } else {
                // create new chatroom
                db.collection('chats').add({ 
                    members: firebase.firestore.FieldValue.arrayUnion($user.uid),
                    created: timestamp, 
                    idea: docId
                }).then((docRef) => {
                    // increase messageCount in newly created chatroom by one
                    incrementMessageCount('chats', docRef.id);
                    incrementMessageCount('ideas', docId);
                    // Save chatroom to users document
                    saveChatroomToUserDoc(docRef.id);
                })
                .catch((error) => console.log(error));
            }
        })
    }
    
    const saveChatroomToUserDoc = (docRefId) => {
        db.collection('users').doc($user.uid).update({
            chats: firebase.firestore.FieldValue.arrayUnion(docRefId)
        }).catch((error) => console.log(error));
    }

    const getAllAuthors = async () => {
        for (const entry of $messages) {
            if($user.uid === entry.uid) continue;
            // Author is previously fetched. Avoid another firestore read
            if(chatUsers.hasOwnProperty(entry.uid)) continue;
            // Author is unknown so far, fetch from firestore and save it
            const otherAuthor = await db.collection('users').doc(entry.uid).get();
            chatUsers[entry.uid] = {name: otherAuthor.data().name, email: otherAuthor.data().email};
        }
    }

    const getAuthor = (entryObj) => {
        // Author is current user
        if($user.uid === entryObj.uid) return $profileData.name;
        // Author is previously fetched. Avoid another firestore read
        if(chatUsers.hasOwnProperty(entryObj.uid)) return chatUsers[entryObj.uid].name;
    }

    const checkPrevious = (entryObj, index) => {
        if(index < 1) return;
        if($messages && $messages[index - 1].uid === entryObj.uid) return true;
    }

    const timeAgo = (seconds) => {
        const date = new Date(seconds * 1000);
        return Dayjs(date).fromNow(true);
    }

</script>

<style src="./Chat.scss" lang="scss"></style>

<section class="Chat">
    <header class="Chat__header">
        <ChatCard 
            title={idea.title} 
            messageCount={idea.messageCount}
            {ideaImageAbsolutePath}>
        </ChatCard>
    </header>
    <div class="Chat__messages">
        {#if $messages.length}
            <ul class="Chat__list">
                {#await getAllAuthors()}
                {:then}
                    {#each $messages as entry, index}
                        <li class="Chat__listitem" class:is-same-author={checkPrevious(entry, index)} class:is-mine={entry.uid === $user.uid}>
                            <small class="Chat__author">{getAuthor(entry)}</small>
                            <span class="Chat__message">
                                {entry.message}
                                <small class="Chat__time">{timeAgo(entry.timestamp?.seconds)}</small>
                            </span>
                        </li>
                    {/each}
                {/await}
            </ul>
        {:else}
            <p class="messages">No messages yet. Break the ice.</p>
        {/if}
    </div>
    <form class="Chat__form">
        <textarea bind:value={message} class="Chat__input" placeholder="Message" use:text_area_resize></textarea>
        <button class="Chat__submit" class:is-hidden={hideSend} type="submit" on:click|preventDefault={addNewMessage}>
            <Icon size={'16px'} viewBox={'0 0 16 16'} data={arrow_right}></Icon>
        </button>
    </form>
</section>